// [crm-iso] Hidden feature ("pesanan"). Panduan hapus/revert: lihat ./TEARDOWN.md
import store from "store";

export const CRM_ISO_SETTING_KEY = "crm_iso_setting";

// Fitur crm_iso_setting hanya diaktifkan untuk endpoint (company) tertentu.
export const ALLOWED_CRM_ISO_ENDPOINTS = [
  "crmkledo.api.kledo.com",
  "crmid.api.kledo.com",
];

// Id role yang masih terlihat oleh agen terpengaruh di tabel /settings/roles,
// per endpoint (id role beda tiap company). Endpoint yang tidak terdaftar di
// sini tidak difilter sama sekali.
export const ISO_VISIBLE_ROLE_IDS_BY_ENDPOINT = {
  // ADMIN CRM, SALES KLEDO, CS KLEDO, SUPPORT KLEDO
  "crmkledo.api.kledo.com": [36, 37, 38, 39],
};

// Nama permission yang masih terlihat oleh agen terpengaruh di form role
// (tambah/ubah), per endpoint. Endpoint yang tidak terdaftar di sini tidak
// difilter sama sekali. Permission baru dari BE otomatis tersembunyi sampai
// ditambahkan ke sini.
export const ISO_VISIBLE_PERMISSIONS_BY_ENDPOINT = {
  // Mengikuti permission role ADMIN CRM (id 36).
  "crmkledo.api.kledo.com": [
    "contacts",
    "contacts_add",
    "contacts_delete",
    "contacts_edit",
    "contacts_group_add",
    "settings",
    "setting_audit",
    "setting_business_flow",
    "setting_company",
    "setting_profile",
    "setting_roles",
    "setting_roles_add",
    "setting_roles_delete",
    "setting_roles_edit",
    "setting_template",
    "setting_users",
    "setting_users_add",
    "setting_users_delete",
    "setting_users_edit",
  ],
};

// Saring tree permission (format GET /roles) ke node yang namanya ada di
// `allowedNames`. Parent tetap dipertahankan bila salah satu child-nya lolos.
export const filterPermissionTree = (nodes, allowedNames) =>
  (nodes || []).reduce((result, node) => {
    const child = filterPermissionTree(node.child, allowedNames);
    if (allowedNames.has(node.name) || child.length) {
      result.push({ ...node, child: child.length ? child : undefined });
    }
    return result;
  }, []);

// Cek apakah endpoint (company) aktif termasuk yang diizinkan memakai fitur ini.
export const isCrmIsoEndpointAllowed = () =>
  ALLOWED_CRM_ISO_ENDPOINTS.includes(store.get("app.endpoint"));

export const defaultCrmIsoConfig = {
  is_active: false,
  affected_providers: [],
  affected_agents: [],
  agent_name_changes: [],
};

// Ubah value option (string JSON / object) menjadi object config yang aman dipakai form.
export const parseCrmIsoSetting = (raw) => {
  if (!raw) return { ...defaultCrmIsoConfig };
  let obj = raw;
  if (typeof raw === "string") {
    try {
      obj = JSON.parse(raw);
    } catch {
      return { ...defaultCrmIsoConfig };
    }
  }
  if (typeof obj !== "object" || obj === null) return { ...defaultCrmIsoConfig };
  return {
    // `?? obj.toggle` untuk migrasi data lama yang masih memakai key `toggle`.
    is_active: Boolean(obj.is_active ?? obj.toggle),
    affected_providers: Array.isArray(obj.affected_providers)
      ? obj.affected_providers
      : [],
    affected_agents: Array.isArray(obj.affected_agents)
      ? obj.affected_agents
      : [],
    agent_name_changes: Array.isArray(obj.agent_name_changes)
      ? obj.agent_name_changes
      : [],
  };
};

// Daftar provider seluler Indonesia beserta prefix nomornya (format lokal 08xx).
// Indonesia belum menerapkan MNP, jadi prefix cukup akurat untuk menebak provider.
// Update di sini bila ada prefix baru / perubahan alokasi.
export const ISO_PROVIDERS = [
  {
    key: "telkomsel",
    name: "Telkomsel",
    prefixes: ["0811", "0812", "0813", "0821", "0822", "0823", "0851", "0852", "0853"],
  },
  {
    key: "indosat",
    name: "Indosat (IM3)",
    prefixes: ["0814", "0815", "0816", "0855", "0856", "0857", "0858"],
  },
  {
    key: "xl",
    name: "XL",
    prefixes: ["0817", "0818", "0819", "0859", "0877", "0878"],
  },
  {
    key: "axis",
    name: "Axis",
    prefixes: ["0831", "0832", "0833", "0838"],
  },
  {
    key: "tri",
    name: "Tri",
    prefixes: ["0895", "0896", "0897", "0898", "0899"],
  },
  {
    key: "smartfren",
    name: "Smartfren",
    prefixes: ["0881", "0882", "0883", "0884", "0885", "0886", "0887", "0888", "0889"],
  },
];

// Samakan format nomor ke lokal 08xx: "628123..." / "+62 812-3..." -> "08123...".
// Nomor berawalan "8" sengaja TIDAK ditambah "0": bisa jadi kode negara lain
// (mis. HK 852..., Jepang 81...) yang malah ketebak provider Indonesia.
export const normalizePhone = (phone) => {
  const digits = String(phone || "").replace(/\D/g, "");
  if (digits.startsWith("62")) return `0${digits.slice(2)}`;
  return digits;
};

// Key provider dari sebuah nomor, atau null bila tidak cocok dengan provider mana pun.
export const getProviderKey = (phone) => {
  const prefix = normalizePhone(phone).slice(0, 4);
  return (
    ISO_PROVIDERS.find((provider) => provider.prefixes.includes(prefix))?.key ||
    null
  );
};

// Apakah nomor kontak percakapan termasuk provider terpengaruh. Utamakan
// contact_wa_number (nomor WA asli), fallback ke contact.phone. Nomor yang tidak
// cocok dengan provider mana pun dianggap tidak termasuk (disembunyikan).
export const isAffectedProviderConversation = (
  conversation,
  affectedProviders = []
) => {
  const providerKey = getProviderKey(
    conversation?.contact_wa_number || conversation?.contact?.phone
  );
  return Boolean(providerKey) && affectedProviders.includes(providerKey);
};

// Gabungkan daftar object dan buang duplikat berdasarkan key tertentu (mempertahankan urutan pertama).
export const uniqueBy = (list, key) => {
  const seen = new Set();
  const result = [];
  (list || []).forEach((item) => {
    if (!item) return;
    const id = item[key];
    if (seen.has(id)) return;
    seen.add(id);
    result.push(item);
  });
  return result;
};
